import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ChevronRight, BookOpen, Award, Building, Loader2, Globe, Languages } from "lucide-react";
import axios from "axios";

const API = window.location.origin;

// Exams where students must pick ONE specific subject (subject-locked)
// All other exams (KVS, CTET, DSSSB etc.) allow browsing ALL subjects
const SUBJECT_SPECIFIC_EXAMS = ['tgt', 'pgt', 'non_teaching'];

// Default category styling (for known categories)
const categoryStyles = {
  tgt: { 
    icon: BookOpen,
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    hoverColor: "hover:border-blue-500",
    level: "Class 10th Level"
  },
  pgt: { 
    icon: Award,
    color: "from-purple-500 to-pink-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    hoverColor: "hover:border-purple-500",
    level: "Class 12th Level"
  },
  non_teaching: { 
    icon: Building,
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    hoverColor: "hover:border-green-500",
    level: "Professional Level"
  }
};

// Default style for new/custom categories
const defaultStyle = {
  icon: BookOpen,
  color: "from-orange-500 to-amber-600",
  bgColor: "bg-orange-50",
  borderColor: "border-orange-200",
  hoverColor: "hover:border-orange-500",
  level: "Custom Category"
};

export default function SubjectSelection({ onComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = category, 2 = subject, 3 = medium
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [apiDown, setApiDown] = useState(false);

  // Fetch categories from API on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/api/notes/lms/syllabus/exams`, { timeout: 8000 });
        const exams = res.data.exams || [];
        if (exams.length === 0) { setApiDown(true); setLoading(false); return; }
        const styledCategories = exams.map(exam => {
          const style = categoryStyles[exam.id] || defaultStyle;
          return {
            id: exam.id,
            label: exam.name || exam.id.toUpperCase().replace(/_/g, ' '),
            fullName: exam.name || exam.id.toUpperCase().replace(/_/g, ' '),
            subjectsCount: exam.subjects_count || 0,
            topicsCount: exam.topics_count || 0,
            ...style
          };
        });
        setCategories(styledCategories);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        setApiDown(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);
  
  // Fetch subjects when category is selected
  const fetchSubjects = async (categoryId) => {
    setLoadingSubjects(true);
    try {
      const res = await axios.get(`${API}/api/notes/lms/syllabus/exams/${categoryId}/subjects`, { timeout: 8000 });
      const subjectsList = res.data.subjects || [];
      setSubjects(subjectsList.map(s => ({
        id: s.id,
        label: s.name || s.id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      })));
    } catch (err) {
      console.error('Failed to fetch subjects:', err);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };
  
  const handleCategorySelect = async (category) => {
    setSelectedCategory(category);
    if (SUBJECT_SPECIFIC_EXAMS.includes(category.id)) {
      // Subject-specific exam (TGT/PGT/Non-Teaching): must pick one subject
      await fetchSubjects(category.id);
      setStep(2);
    } else {
      // All-subjects exam (KVS, CTET, DSSSB etc.): student studies all subjects
      // Skip subject selection step, set subject as "all"
      setSelectedSubject({ id: 'all', label: 'All Subjects' });
      setStep(3);
    }
  };
  
  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setStep(3); // Go to medium selection
  };
  
  const handleMediumSelect = (language) => {
    // Save to localStorage with language
    const selection = {
      category: selectedCategory.id,
      categoryLabel: selectedCategory.label,
      subject: selectedSubject.id,
      subjectLabel: selectedSubject.label,
      language,
      selectedAt: new Date().toISOString()
    };
    localStorage.setItem('user_subject_selection', JSON.stringify(selection));
    
    // Update selected_category in stored user to match new selection
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.selected_category = selectedCategory.id;
      localStorage.setItem('user', JSON.stringify(storedUser));
      
      // Also update server-side category
      const sessionToken = localStorage.getItem('session_token');
      if (storedUser.id && sessionToken) {
        const formData = new FormData();
        formData.append('user_id', storedUser.id);
        formData.append('session_token', sessionToken);
        formData.append('selected_category', selectedCategory.id);
        fetch(`${API}/api/auth/update-selected-category`, { method: 'POST', body: formData }).catch(() => {});
      }
    } catch (e) {
      // ignore
    }
    
    onComplete(selection);
    
    // Navigate directly to the subject page (not /dashboard which redirects to public /category)
    if (selectedSubject.id === 'all') {
      // For "all subjects" exams (KVS PRT etc.), go to category subjects page
      navigate(`/category/${selectedCategory.id}`);
    } else {
      // For specific subject exams (TGT/PGT/Non-Teaching), go directly to subject page
      navigate(`/${selectedCategory.id}/${selectedSubject.id}`);
    }
  };

  const handleBack = () => {
    if (step === 3 && SUBJECT_SPECIFIC_EXAMS.includes(selectedCategory?.id)) {
      // TGT/PGT/Non-Teaching: go back to subject selection
      setStep(2);
      setSelectedSubject(null);
    } else {
      // Go back to category selection
      setStep(1);
      setSelectedCategory(null);
      setSelectedSubject(null);
      setSubjects([]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-xl mb-6">
            <GraduationCap className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            EMRS KVS NVS
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Descriptive Paper Practice
            </span>
          </h1>
          <p className="text-gray-600 text-lg max-w-xl mx-auto">
            {step === 1 
              ? "अपनी परीक्षा श्रेणी चुनें / Select your exam category to get personalized content"
              : step === 2
              ? `${selectedCategory?.label} - अपना विषय चुनें / Select your subject`
              : "अपना माध्यम चुनें / Choose Your Medium"
            }
          </p>
        </div>

        {/* Step 1: Category Selection */}
        {step === 1 && (
          loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading categories...</span>
            </div>
          ) : apiDown ? (
            <div className="flex flex-col items-center justify-center py-24 text-center space-y-6">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">We are updating Features</h2>
                <p className="text-gray-500 text-lg">Please wait, we will come back soon 🙏</p>
                <p className="text-gray-400 text-sm mt-3">हम नई सुविधाएं जोड़ रहे हैं, थोड़ी देर में वापस आएं</p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6" data-testid="category-selection">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Card
                    key={cat.id}
                    className={`cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 ${cat.borderColor} ${cat.hoverColor} ${cat.bgColor}`}
                    onClick={() => handleCategorySelect(cat)}
                    data-testid={`category-${cat.id}`}
                  >
                    <CardContent className="py-8 text-center">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <Badge className={`mb-3 bg-white border ${cat.borderColor}`}>{cat.label}</Badge>
                      <h3 className="font-bold text-gray-800 text-lg mb-1">{cat.fullName}</h3>
                      <p className="text-sm text-gray-500">{cat.level}</p>
                      {cat.subjectsCount > 0 && (
                        <p className="text-xs text-gray-400 mt-1">{cat.subjectsCount} subjects, {cat.topicsCount} topics</p>
                      )}
                      <div className="mt-4 flex items-center justify-center text-gray-400">
                        <span className="text-sm">Select</span>
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        )}

        {/* Step 2: Subject Selection */}
        {step === 2 && selectedCategory && (
          <div data-testid="subject-selection">
            {/* Back button */}
            <Button 
              variant="outline" 
              className="mb-6" 
              onClick={handleBack}
              data-testid="back-to-category"
            >
              ← Back to Categories
            </Button>
            
            {/* Selected category badge */}
            <div className="mb-6 text-center">
              <Badge className={`px-4 py-2 text-lg bg-gradient-to-r ${selectedCategory.color} text-white`}>
                {selectedCategory.label} - {selectedCategory.fullName}
              </Badge>
            </div>
            
            {/* Subjects grid */}
            {loadingSubjects ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Loading subjects...</span>
              </div>
            ) : subjects.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No subjects found for this category.</p>
                <p className="text-sm mt-2">Please add subjects in LMS Manager.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {subjects.map((subject) => (
                  <Card
                    key={subject.id}
                    className={`cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg border-2 border-transparent hover:border-blue-500 bg-white`}
                    onClick={() => handleSubjectSelect(subject)}
                    data-testid={`subject-${subject.id}`}
                  >
                    <CardContent className="py-6 text-center">
                      <BookOpen className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                      <h3 className="font-semibold text-gray-800">{subject.label}</h3>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Medium Selection */}
        {step === 3 && selectedCategory && selectedSubject && (
          <div data-testid="medium-selection">
            {/* Back button */}
            <Button 
              variant="outline" 
              className="mb-6" 
              onClick={handleBack}
              data-testid="back-to-subject"
            >
              {SUBJECT_SPECIFIC_EXAMS.includes(selectedCategory?.id) ? '← Back to Subjects' : '← Back to Categories'}
            </Button>

            {/* Selected info */}
            <div className="mb-8 text-center">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Badge className={`px-3 py-1 bg-gradient-to-r ${selectedCategory.color} text-white`}>
                  {selectedCategory.label}
                </Badge>
                {selectedSubject.id !== 'all' && (
                  <>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <Badge variant="outline" className="px-3 py-1">
                      {selectedSubject.label}
                    </Badge>
                  </>
                )}
                {selectedSubject.id === 'all' && (
                  <>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <Badge variant="outline" className="px-3 py-1 border-green-400 text-green-700">
                      All Subjects (PRT / सभी विषय)
                    </Badge>
                  </>
                )}
              </div>
              <p className="text-gray-600 mt-4 text-base">
                आपको content किस माध्यम में चाहिए? / In which medium do you want content?
              </p>
            </div>

            {/* Medium cards */}
            <div className="grid grid-cols-2 gap-6 max-w-lg mx-auto">
              {/* English Medium */}
              <Card
                className="cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 border-blue-200 hover:border-blue-600 bg-gradient-to-br from-blue-50 to-indigo-50 group"
                onClick={() => handleMediumSelect('english')}
                data-testid="medium-english"
              >
                <CardContent className="py-10 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover:shadow-blue-300 transition-shadow">
                    <span className="text-4xl font-black text-white">A</span>
                  </div>
                  <h3 className="font-bold text-gray-800 text-xl mb-1">English</h3>
                  <p className="text-sm text-blue-600 font-medium">English Medium</p>
                  <p className="text-xs text-gray-500 mt-2">Notes & MCQs in English</p>
                </CardContent>
              </Card>

              {/* Hindi Medium */}
              <Card
                className="cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 border-orange-200 hover:border-orange-600 bg-gradient-to-br from-orange-50 to-amber-50 group"
                onClick={() => handleMediumSelect('hindi')}
                data-testid="medium-hindi"
              >
                <CardContent className="py-10 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg group-hover:shadow-orange-300 transition-shadow">
                    <span className="text-4xl font-black text-white">अ</span>
                  </div>
                  <h3 className="font-bold text-gray-800 text-xl mb-1">हिंदी</h3>
                  <p className="text-sm text-orange-600 font-medium">Hindi Medium</p>
                  <p className="text-xs text-gray-500 mt-2">नोट्स और MCQ हिंदी में</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className="mt-10 text-center text-sm text-gray-500">
          <p>आप बाद में Dashboard से अपना विषय बदल सकते हैं</p>
          <p>You can change your subject later from the Dashboard</p>
        </div>
      </div>
    </div>
  );
}

// Helper function to get saved selection
export function getSavedSubjectSelection() {
  try {
    const saved = localStorage.getItem('user_subject_selection');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error reading subject selection:', e);
  }
  return null;
}

// Helper function to clear selection
export function clearSubjectSelection() {
  localStorage.removeItem('user_subject_selection');
  // Also clear notes state to prevent old topics from loading
  localStorage.removeItem('notes_state');
  // Clear the category lock so user can switch categories freely
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser.selected_category) {
      delete storedUser.selected_category;
      localStorage.setItem('user', JSON.stringify(storedUser));
    }
  } catch (e) {
    // ignore
  }
}
